import PropTypes from 'prop-types';
import { Image as CloudImage } from 'cloudinary-react';

import { getEnvironment } from '../../lib/utils';
import { parseCloudName } from '../../lib/cloudinary.js';
import { wbAssetServiceEnabled } from '../../lib/wb-asset';

import { mergeTransformationParams } from './helpers';
import { WbAssetImage } from './WbAssetImage.js';

export { defaultTransformationParams } from './helpers';

const { CLOUDINARY_CLOUD_NAME } = getEnvironment();

/**
 * Wraps the cloudinary-react Image tag with some additional preprocesing.  By default, the
 * cloudinary transformations specified in defaultTransformationParams are applied, but can
 * be overridden via the transformation option.
 *
 * @param {object} opts
 * @param {object[]} opts.children - Children tags that can be used in conjunction
 * with react-cloudinary's <Image> tag, such as <Transformation> (see {@link https://cloudinary.com/documentation/react_integration#installation_and_setup|sdk docs}).
 * @param {object} opts.image - This is an object that contains either a url or a publicId.
 * @param {*} opts.facebookId
 * @param {number} opts.width - Cloudinary width transformation (deprecated, use "transformation").
 * @param {number} opts.height - Cloudinary height transformation (deprecated, use "transformation").
 * @param {object} opts.style - CSS Styling for the Image tag.
 * @param {string} opts.crop - Cloudinary crop transformation (deprecated, use "transformation").
 * @param {string} opts.dpr - Cloudinary dpr transformation (deprecated, use "transformation").
 * @param {string} opts.className - CSS className passed on to the Image tag.
 * @param {boolean} opts.draggable - Passed to the Image tag.
 * @param {object} opts.transformation - Cloudinary transformations passed on to the Image tag.
 * Keys here will override the matching inline equivalents: width, height, crop, and dpr.
 * This is true even if the value in the transformation object is undefined/falsy.
 * @param {string} opts.alt - Alt attribute passed to the Image tag.
 *
 * Note: the CloudinaryImage "transforms" property (which is passed on to
 * the Cloudinary Image "component") cannot be used to specify a gradient
 * effect. Doing so produces an invalid Cloudinary URL.
 *
 * Per {@link https://support.cloudinary.com/hc/en-us/articles/203585582-How-to-use-and-customize-the-Gradient-Fade-effect-|their docs},
 * the "effect" parameters need to be in a separate path part of the url.
 *
 *     /[...]/images/upload/w_200/e_gradient_fade/v1/staging/.
 *
 * Using the transforms property places the gradient effect in the same
 * path part.
 *
 *     /[...]/image/upload/c_fill,dpr_2.0,e_gradient_fade,y_-0.4,b_rgb:000000,f_auto,w_200,q_auto/v1/staging/.
 *
 * Such an url yields an error from the Cloudinary API (HTTP 400).
 *
 *     Maximum image width/height is 65500. Requested 1601600x1601600.
 *
 * Using child <Transform> tags allows specifying the transforms in a way which
 * produce the correct URL.
 * @returns {JSX.Element}
 */
export const CloudinaryImage = ({
  children,
  image,
  facebookId,
  width,
  height,
  style,
  crop,
  dpr,
  className,
  draggable,
  transformation = {},
  alt,
}) => {
  const { url, publicId } = image || {};
  const cloudName = url ? parseCloudName(url) : CLOUDINARY_CLOUD_NAME;

  const isSvg = !!publicId?.match(/\.svg$/);

  if (!facebookId && !publicId) return null;

  const withProtocol = publicId?.startsWith('//res.cloudinary.com')
    ? `https:${publicId}`
    : publicId;

  // From start we use waybetter avatar
  let imagePublicId = withProtocol;
  let imageType = 'upload';

  if (facebookId) {
    imagePublicId = facebookId;
    imageType = 'facebook';
  }

  return wbAssetServiceEnabled() ? (
    <WbAssetImage
      publicId={imagePublicId}
      className={className}
      draggable={draggable}
      style={style}
      alt={alt}
      type={imageType}
      // If any <Transformation> child tags are present,
      // the transformation prop here is ignored.
      transformation={
        isSvg
          ? null
          : mergeTransformationParams({
              inline: { crop, dpr, height, width },
              transformation,
            })
      }
    >
      {children}
    </WbAssetImage>
  ) : (
    <CloudImage
      publicId={imagePublicId}
      className={className}
      draggable={draggable}
      style={style}
      alt={alt}
      cloudName={cloudName}
      type={imageType}
      secure={true}
      // If any <Transformation> child tags are present,
      // the transformation prop here is ignored.
      transformation={
        isSvg
          ? null
          : mergeTransformationParams({
              inline: { crop, dpr, height, width },
              transformation,
            })
      }
    >
      {children}
    </CloudImage>
  );
};

CloudinaryImage.propTypes = {
  image: PropTypes.shape({
    url: PropTypes.string,
    publicId: PropTypes.string,
  }),
  facebookId: PropTypes.string,
  transforms: PropTypes.object,
  width: PropTypes.number,
  height: PropTypes.number,
  style: PropTypes.object,
  crop: PropTypes.oneOf(['fill', 'fit', 'scale', 'thumb']),
  className: PropTypes.string,
  draggable: PropTypes.bool,
  alt: PropTypes.string,
};

CloudinaryImage.defaultProps = {
  draggable: true,
  crop: 'scale',
};

export default CloudinaryImage;
